
import PopLayout from '@xt/www/components/classhour/layout/index.vue'
import ContentEditor from '@xt/www/components/classhour/editor/index.vue'
import { Component, Vue, Prop, Watch, Emit, Ref } from 'vue-property-decorator'
import type { DairyOption, HomeworkOption } from '@xt/client/entities/course-v2/user-content'
import type { ConfirmModalOptions, Moment, ModalDataType, GuildDetail } from '@xt/client/entities/kindred-spirit/kindred-spirit'
import { EnumCityStatus, EnumJoinResponseType } from '@xt/client/entities/kindred-spirit/guild'
import IntroPanel, { EnumStatus } from '../../components/IntroPanel/index.vue'
import MomentCard from '../../components/MomentCard/index.vue'
import UserContentPop from '@xt/www/components/classhour/pop-content/index.vue'
import VirtualWaterfall from '@xt/www/components/virtualWaterFall/index.vue'
import InfiniteLoading from '@xt/www/components/infinite-loadingv2/index.vue'
import { Observer } from 'mobx-vue'
import { Bind, Debounce } from 'lodash-decorators'
import ConfirmModal from '../../components/ConfirmModal/index.vue'
import { MomentSourceType } from '@xt/client/enums/moment'
import { EnumTongHaoStatus } from '@xt/client/enums/kindred-spirit'
import { WaterfallMoment } from '@xt/client/entities/course-v2/types'
import { reportEntryLog } from '@xt/client/utils/log'
import EntryModal from '../../components/EntryModal/index.vue'

@Observer
@Component({
  components: {
    UserContentPop,
    IntroPanel,
    MomentCard,
    VirtualWaterfall,
    InfiniteLoading,
    ConfirmModal,
    PopLayout,
    ContentEditor,
    EntryModal
  },
  async asyncData(ctx) {
    await ctx.store.$tonghao.loadJSON()
    const cityId = ctx.params.id
    const isLogin = !!ctx.store.$storeUser.UserInfo.id
    const data = await ctx.store.$tonghao.Guild.initPage(cityId, isLogin)

    if (!data) {
      ctx.redirect('/')
      return
    }

    const { detail, hasJoin, isJoinCity, isManager } = data

    return {
      cityId,
      currentCityInfo: detail,
      hasJoin,
      isJoinCity,
      isManager
    }
  },
  validate({ params }) {
    return /^\d+$/.test(params.id)
  }
})
export default class CityPage extends Vue {
  currentCityInfo: GuildDetail
  needLogin: boolean = false
  cityId: string
  hasJoin: boolean // 是否加入分桐
  isJoinCity: boolean // 当前分桐是否是已加入的分桐
  isManager: boolean // 是否为当前分桐管理员

  guildOptions = {
    type: 'guild',
    showBackButton: false,
    contentRequired: false,
    imageRequired: true,
    maxImageSize: 9,
    editorTitle: '日常内容',
    uploaderTitle: '添加图片',
    contentPlaceholder: '在这里，与桐好交流的心得、笔墨练习的收获、雅会相聚的趣事，都可以随手记录、自在分享。'
  }

  @Ref()
  userContentPop: {
    showPop: (options: HomeworkOption | DairyOption) => void
    hidePop: () => void
  }

  @Ref()
  confirmModal: {
    show: (options: ConfirmModalOptions) => Promise<boolean>
  }

  @Ref()
  editor?: {
    getFormStatus(): { hasImage: boolean; hasContent: boolean; hasTitle: boolean }
  }
  @Ref()
  popLayout: {
    open: () => void
    hide: () => void
  }

  get hideWaterfall() {
    if (!this.currentCityInfo) return true

    if (this.currentCityInfo.status === EnumCityStatus.Inited) return false

    if (this.currentCityInfo.recruitStatus === true) {
      return EnumStatus.Recruit
    }

    if (this.currentCityInfo.status === EnumCityStatus.Assistance) {
      return !this.hasJoin ? EnumStatus.Assistance : EnumStatus.Share
    }

    return false
  }

  get body() {
    return {
      sourceId: this.cityId,
      sourceType: MomentSourceType.Guild
    }
  }

  get EnumStatus() {
    return EnumStatus
  }

  get EnumCityStatus() {
    return EnumCityStatus
  }

  get UserInfo() {
    return this.$store.$storeUser.UserInfo
  }

  get GuildStore() {
    return this.$store.$tonghao.Guild
  }

  get TonghaoStore() {
    return this.$store.$tonghao
  }

  get pagePagination() {
    return this.TonghaoStore.pagePagination
  }

  get UserStore() {
    return this.$store.$storeUser
  }

  compactImageSize(originWidth: number, originHeight: number) {
    const limitWidth = 194
    const shouldHeigth = Math.floor((limitWidth * originHeight) / originWidth)

    return [limitWidth, shouldHeigth]
  }

  getImageHeight(originWidth: number, originHeight: number) {
    return Math.max(100, Math.min(248, this.compactImageSize(originWidth, originHeight)[1]))
  }

  getTextWidth(text, font = '14px sans-serif') {
    if (!text) return null
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.font = font
    return ctx.measureText(text).width
  }

  // 获取动态高度
  getComptedHeightList(list) {
    return this.toJS(list).map(item => {
      let totalHeight: number = 0

      // 计算图片高度
      totalHeight +=
        Array.isArray(this.toJS(item.pictures)) && item.pictures.length > 0
          ? this.getImageHeight(item.pictures[0].originWidth, item.pictures[0].originHeight)
          : 248
      // 计算 title 间距
      totalHeight += item.title || item.content ? (item.sourceDescription ? 8 : 10) : 0
      // 计算 title 高度，如果超过200（一行文字宽度），则高度为36，否则为18
      totalHeight += item.title || item.content ? ((this.getTextWidth(item.title || item.content) ?? 0) > 200 ? 36 : 18) : 0
      // 计算 card-info 间距 + 高度 + 底部间距
      totalHeight += 12 + 28 + 12

      return {
        ...item,
        height: totalHeight
      }
    })
  }

  handleClickMoment(momentId: string) {
    if (!momentId) return

    this.userContentPop?.showPop({
      type: 'dairy',
      momentId
    })
  }

  // 卡片点赞事件
  @Debounce(350, { leading: true, trailing: false })
  @Bind()
  async handleLikeMoment(item: Moment) {
    // 未登录防御
    if (!this.UserInfo.id) {
      this.$store.$storeUser.onToggleVisible(true)
      return
    }

    try {
      await this.TonghaoStore.onLikeMoment(item)
      this.$message.success('点赞成功')
    } catch (e) {
      if (e instanceof Error) {
        this.$message.error(e.message)
      }
    }
  }

  handleClosed() {
    if (this.needLogin) {
      this.$store.$storeUser.onToggleVisible(true)
      this.needLogin = false
    }
  }

  handleOpenLogin() {
    this.needLogin = true
    this.userContentPop.hidePop()
  }

  // 弹窗内部删除事件
  handleRemoveSuccess(data: { type: 'dairy'; momentId: string }) {
    if (data.type === 'dairy' && data.momentId) {
      // 删除缓存的数据
      this.TonghaoStore.removeItem(data.momentId)
    }
  }

  // 弹窗内部收藏事件
  handleLikeSuccess(data: { momentId: string }) {
    if (data.momentId) {
      this.TonghaoStore.likeItem(data.momentId)
    }
  }

  handleClickBackToTop() {
    const el2 = document.getElementById('page-scroll-target-two')

    if (!el2) return

    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  handleWriteDairy() {
    this.popLayout.open()
  }

  handleSuccess({ payload }: { payload: WaterfallMoment }) {
    this.popLayout.hide()

    // 将新的日记插入到瀑布流[发现]第一个位置处
    const oldList = [...this.toJS(this.pagePagination.dataSource)]
    const newList = [payload, ...oldList]
    this.pagePagination.updateSource(newList)
  }

  handleClickWriteDairyOutside() {
    if (this.editor) {
      const { hasContent, hasImage, hasTitle } = this.editor.getFormStatus()

      if (hasContent || hasImage || hasTitle) {
        this.$confirmV2({
          desc: '你的日记还未提交',
          okText: '坚持离开',
          cancelText: '取消',
          clickToClose: false,
          onOk: () => {
            this.popLayout.hide()
          },
          onException: () => {
            this.popLayout.hide()
          }
        })
        return
      }
    }

    this.popLayout.hide()
  }

  onJoinFail() {
    this.confirmModal.show({
      title: `还不能加入哦~`,
      description: '仅限报名过林曦老师亲授的直播课程，或是林曦老师监制的教练日课的桐学方可申请加入。',
      layoutClassName: 'join-fail-confirm-modal',
      confirmedHide: true,
      confirmText: '确认',
      hideCancel: true
    })
  }

  async reloadPage() {
    try {
      const data = await this.GuildStore.initPage(this.cityId, true)

      if (!data) return

      const { detail, hasJoin, isJoinCity, isManager } = data

      this.currentCityInfo = {
        ...detail
      }
      this.hasJoin = hasJoin
      this.isJoinCity = isJoinCity
      this.isManager = isManager
    } catch (error) {
      reportEntryLog('topic-guild-id.init-page.fail', { guildId: this.cityId, errMsg: error.message })
    }
  }

  async onExit() {
    this.$router.push('/topic')
    this.$msg('退出成功', 'success')
  }

  async onJoinSuccess() {
    await this.reloadPage()
    this.$msg('加入成功~', 'success')
  }

  async openAssistantModal(data: ModalDataType) {
    const res = await this.confirmModal.show({
      title: `为${data.cityName}分桐助力`,
      description: `助力成功后将自动加入${data.cityName}分桐，我们会选取热力值最高的1-2个城市，优先推动分桐开放。`,
      layoutClassName: 'assistant-confirm-modal',
      confirmedHide: true,
      confirmText: '立即助力',
      cancelText: '稍后再说',
      backgroundUrl: this.$store.$global.static + '/images/topic/pc/assistant-pop-bg.png'
    })
    if (res === true) {
      const res = await this.GuildStore.joinGuild(data.id)
      if (res === EnumJoinResponseType.Success) {
        this.onJoinSuccess()
      } else if (res === EnumJoinResponseType.NoAuth) {
        this.onJoinFail()
      }
    }
  }
  async openRecruitModal(data: ModalDataType) {
    const res = await this.confirmModal.show({
      title: `成为${data.cityName}分桐负责人`,
      description: `报名参与负责人招募，即视为自动申请加入该分桐。待负责人招募工作全部完成后，${data.cityName}分桐将正式成立。`,
      layoutClassName: 'recruit-confirm-modal',
      confirmedHide: true,
      confirmText: '确认报名',
      cancelText: '再想想',
      backgroundUrl: this.$store.$global.static + '/images/topic/pc/recruit-pop-bg.png'
    })

    if (res === true) {
      const res = await this.GuildStore.joinGuild(data.id)
      if (res === EnumJoinResponseType.Success) {
        this.onJoinSuccess()
        if (!this.TonghaoStore.dataSource) return
        window.open(this.TonghaoStore.dataSource.recruitUrl, '_blank')
      } else if (res === EnumJoinResponseType.NoAuth) {
        this.onJoinFail()
      }
    }
  }
  async openJoinModal(data: ModalDataType) {
    const res = await this.confirmModal.show({
      title: `${data.cityName}分桐`,
      description: '桐学仅可选择一座常住城市加入，与桐好笔墨相伴，雅会常聚。',
      layoutClassName: 'join-confirm-modal',
      confirmedHide: true,
      confirmText: '确认加入',
      cancelText: '再想想',
      backgroundUrl: this.$store.$global.static + '/images/topic/pc/assistant-pop-bg.png'
    })

    if (res === true) {
      const res = await this.GuildStore.joinGuild(data.id)
      if (res === EnumJoinResponseType.Success) {
        this.onJoinSuccess()
      } else if (res === EnumJoinResponseType.NoAuth) {
        this.onJoinFail()
      }
    }
  }

  updateGuildOptions() {
    if (!this.currentCityInfo) return
    this.guildOptions['guildId'] = this.currentCityInfo.id
  }

  created(): void {
    this.TonghaoStore.mapPagePagination(EnumTongHaoStatus.FenTong)
    this.updateGuildOptions()
  }

  mounted(): void {}

  updated(): void {}

  destroyed(): void {}
}
